import React from 'react';
import styled from 'styled-components';

type Props = {
  value?: string;
  defaultValue?: string;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
  placeholder?: string;
  tips?: string;
  onSave?: () => void;
  isSaving?: boolean;
  onCancel?: () => void;
  subtitle?: string;
  error?: string;
  onFix?: () => void;
  fixLabel?: string;
};

const Textarea = (props: Props) => {
  const {
    value,
    defaultValue,
    onChange,
    placeholder,
    tips,
    onSave,
    isSaving,
    onCancel,
    subtitle,
    error,
    onFix,
    fixLabel,
  } = props;
  const isChanged = value !== defaultValue;
  const valueLength = value?.length || 0;
  return (
    <Container isSaving={isSaving}>
      {subtitle && <Subtitle>{subtitle}</Subtitle>}
      <div className="input-wrapper">
        <textarea
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={valueLength > 1024 ? 'xl' : valueLength > 512 ? 'lg' : ''}
        />
      </div>
      {tips && <div className="tips">{tips}</div>}
      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}
      <div className="actions">
        {onCancel && (
          <button
            disabled={isSaving || !isChanged}
            onClick={onCancel}
            className="outlined"
          >
            Cancel
          </button>
        )}
        {error && onFix && (
          <button disabled={isSaving} onClick={onFix} className="outlined">
            {fixLabel || 'Fix'}
          </button>
        )}
        <button disabled={isSaving || !isChanged} onClick={onSave}>
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </Container>
  );
};

export default Textarea;

const Container = styled.div<{ isSaving?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 24px;

  .input-wrapper {
    display: flex;
    flex-direction: column;
    gap: 24px;
    border: 1px solid var(--border);
    border-radius: 8px;

    &:focus-within {
      border-color: var(--text-secondary);
    }

    > textarea {
      color: var(--text-secondary);
      font-family: var(--font-primary);
      font-size: var(--text-sm);
      font-weight: var(--font-weight-regular);
      line-height: 1.5;
      background: transparent;
      outline: none;
      border: none;
      resize: none;
      padding: 24px;
      min-height: 128px;
      &.lg {
        min-height: 256px;
      }
      &.xl {
        min-height: 512px;
      }
      &::placeholder {
        color: var(--text-muted);
      }
      & {
        scrollbar-color: var(--gray-400) transparent;
      }
      &::-webkit-scrollbar {
        width: 8px;
      }
      &::-webkit-scrollbar-thumb {
        background-color: transparent;
      }
    }
  }

  .tips {
    color: var(--text-muted);
    font-size: var(--text-xs);
    font-weight: var(--font-weight-regular);
    font-style: italic;
    margin-top: -14px;
  }

  .error {
    color: var(--destructive);
    font-size: var(--text-sm);
    margin-top: -14px;
  }

  .actions {
    align-self: flex-end;
    display: flex;
    gap: 16px;

    > button {
      display: block;
      height: 48px;
      padding: 12px 24px;
      border-radius: 8px;
      background: var(--primary);
      color: var(--background);
      font-size: var(--text-sm);
      font-weight: var(--font-weight-bold);
      border: none;
      transition: var(--transition-normal);
      opacity: ${({ isSaving }) => (isSaving ? 0.6 : 1)};
      cursor: ${({ isSaving }) => (isSaving ? 'not-allowed' : 'pointer')};
      &.outlined {
        color: var(--primary);
        background: inherit;
        border: 1px solid var(--primary);
      }
      &:disabled {
        color: var(--text-muted);
        border: 1px solid var(--text-muted);
        background: transparent;
        cursor: not-allowed;
      }
      &:hover:not(:disabled) {
        filter: brightness(1.2) saturate(1.2);
      }
    }
  }
`;

const Subtitle = styled.div`
  color: var(--text-secondary);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-style: normal;
  font-weight: var(--font-weight-regular);
  line-height: 140%; /* 19.6px */
  margin-bottom: -14px;
`;
